import { Component } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { NgbActiveModal, NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { AuthService } from '../../../../auth/auth.service';
import { UserDto } from '../../../../auth/user.dto';
import { CommentComponent } from '../../../../shared/comment/comment.component';
import { ConfirmationComponent } from '../../../../shared/confirmation/confirmation.component';
import { Achievement, labelsByAchievement } from '../../../../shared/models/enums/achievement.enum';
import { ContractStatus, labelsByContractStatus } from '../../../../shared/models/enums/contract-status.enum';
import {
  ContractType,
  WorkflowedContractType,
  labelsByAllContractType,
  labelsByRsaTypedContractType,
  labelsByWorkflowedTypedContractType,
} from '../../../../shared/models/enums/contract-type.enum';
import { ContractsService } from '../../../../shared/services/contracts.service';
import { DateService } from '../../../../shared/services/date.service';
import { StringUtils } from '../../../../shared/services/stringUtils.service';
import { SignatureComponent } from '../../../../shared/signature/signature.component';
import { ToastService } from '../../../../shared/toast/toast.service';
import { ContractTraceabilityStatus } from '../../../../shared/traceability/contract-traceability-status.enum';
import { DisplayTraceabilityDto } from '../../../../shared/traceability/display-traceability.dto';
import { TraceabilityDto } from '../../../../shared/traceability/traceability.dto';
import { DateValidators } from '../../../../shared/validators/date.validators';
import { BeneficiaryDto } from '../../../dto/beneficiary.dto';
import { ContractDto } from '../contract.dto';
import { DatesNewContractDto } from './dates-new-contract-dto';
import { TPermissionContext } from '../../../../shared/permissions/permissions';

@Component({
  selector: 'app-contract-details',
  templateUrl: './contract-details.component.html',
  styleUrls: ['./contract-details.component.scss'],
})
export class ContractDetailsComponent {
  public contract: ContractDto;
  public contractDetailsForm: UntypedFormGroup;
  public isReadingMode: boolean = false;
  public isUpdatingMode: boolean = false;
  public beneficiary: BeneficiaryDto;
  public submitted: boolean = false;
  public isWorkflowedContract: boolean = false;
  public workflowedContractPermissionContext: TPermissionContext;
  public isCreation: boolean = false;
  public isContractEnded: boolean = false;
  public traceability: DisplayTraceabilityDto[] = [];
  public traceabilityStatus = ContractTraceabilityStatus;
  public user: UserDto;

  private readonly destroy$: Subject<void> = new Subject();
  public contractTypes: string[] = labelsByAllContractType;
  public readonly statusMap: string[] = labelsByContractStatus;
  public readonly achievements: Map<Achievement, string> = labelsByAchievement;
  public readonly StringUtils: StringUtils = StringUtils;
  private readonly currentDate: Date;
  private contractTypeSelected: boolean;
  public datesNewContract: DatesNewContractDto;

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly modal: NgbActiveModal,
    private readonly contractsService: ContractsService,
    private readonly toastService: ToastService,
    private readonly dateService: DateService,
    private readonly modalService: NgbModal,
    private readonly authService: AuthService,
  ) {
    this.currentDate = this.dateService.getCurrent();
  }

  public async onInit(beneficiary: BeneficiaryDto, contract?: ContractDto): Promise<void> {
    this.contractTypeSelected = contract ? true : false;
    this.user = await this.authService.getUser();

    this.beneficiary = beneficiary;
    this.contract = contract;
    this.traceability = this.contract ? this.contract.traceability : [];
    this.isWorkflowedContract = labelsByWorkflowedTypedContractType.includes(this.contract?.contractType);
    this.contractTypes = !this.contract
      ? this.user?.groups.includes('TERRITORY')
        ? labelsByRsaTypedContractType
        : this.contractTypes
      : this.isWorkflowedContract
        ? this.contractTypes
        : labelsByRsaTypedContractType;
    this.workflowedContractPermissionContext =
      this.contract?.contractType === ContractType.StartingIERContract
        ? 'CONTRACT_IER'
        : this.contract?.contractType === ContractType.SocioPro
          ? 'CONTRACT_SOCIO_PRO'
          : undefined;
    this.isCreation = !this.contract;

    this.datesNewContract = await this.contractsService.getDatesContract(this.beneficiary.id);

    await this.loadForm();

    this.contractDetailsForm.controls.status.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: ContractStatus) => this.onStatusChanged(value));
    this.contractDetailsForm.controls.contractType.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((value: ContractType) => this.onTypeChanged(value));
  }

  /**
   * Charger formulaire de consultation contrat
   * @param contractId
   */
  private async loadForm(): Promise<void> {
    this.contractDetailsForm = this.formBuilder.group(
      {
        beginDate: [
          { value: this.contract?.beginDate, disabled: !this.contractTypeSelected },
          [Validators.required, DateValidators.dateFormatValidator],
        ],
        endDate: [{ value: this.contract?.endDate, disabled: !this.contractTypeSelected }, [Validators.required, DateValidators.dateFormatValidator]],
        contractType: [{ value: this.contract?.contractType, disabled: this.isWorkflowedContract }, [Validators.required]],
        mainPurpose: [{ value: this.contract?.mainPurpose, disabled: !this.contractTypeSelected }, Validators.required],
        status: [{ value: this.contract?.status, disabled: !this.contractTypeSelected }, Validators.required],
        achievement: [{ value: this.contract?.achievement, disabled: !this.contractTypeSelected }],
        project: [{ value: this.contract?.project, disabled: !this.contractTypeSelected }],
        actionsToPerform: [{ value: this.contract?.actionsToPerform, disabled: !this.contractTypeSelected }],
        statusDate: [{ value: this.contract?.statusDate, disabled: true }],
      },
      {
        validators: [DateValidators.datesOrderValidator('beginDate', 'endDate')],
      },
    );
    this.isReadingMode = !!this.contract;
    this.isUpdatingMode = this.contract ? this.contract.status !== 'Done' : true;
    if (this.contract) {
      this.onTypeChanged(this.contract.contractType as ContractType);
      this.onStatusChanged(this.contract.status as ContractStatus, true);
    }
  }

  public cancel(): void {
    this.contractDetailsForm.reset();
    this.modal.close();
  }

  public async onSubmit(): Promise<void> {
    this.submitted = true;
    if (!this.contractDetailsForm.valid) {
      this.toastService.show('La sauvegarde a échoué, veuillez vérifier les données.', 'danger');
      return;
    }

    const toSaveContract = new ContractDto(
      this.contract ? this.contract.id : undefined,
      this.contractDetailsForm.controls.beginDate.value,
      this.contractDetailsForm.controls.endDate.value,
      this.contractDetailsForm.controls.contractType.value,
      this.contractDetailsForm.controls.mainPurpose.value,
      this.contractDetailsForm.controls.status.value,
      this.contractDetailsForm.controls.achievement.value,
      this.contractDetailsForm.controls.project.value,
      this.contractDetailsForm.controls.actionsToPerform.value,
      this.contractDetailsForm.controls.statusDate.value,
      this.beneficiary.id,
    );
    if (this.contract?.id) {
      await this.updateContract(toSaveContract);
    } else {
      await this.createContract(toSaveContract);
    }
  }

  private async createContract(toSaveContract: ContractDto) {
    try {
      await this.contractsService.saveContract(toSaveContract);
      this.toastService.show('Le contrat a été ajouté avec succès.', 'success');
      this.modal.close();
    } catch (error) {
      if (error.status === 470) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas d'accompagnement.", 'danger');
      } else if (error.status === 471) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas de référent.", 'danger');
      } else if (error.status === 472) {
        this.toastService.show("Impossible de créer le contrat, le bénéficiaire n'a pas de structure.", 'danger');
      } else if (error.status === 473) {
        this.toastService.show(
          `Création impossible, un contrat ${this.contractTypeLabel(toSaveContract.contractType)} non terminé existe déjà sur la période.`,
          'danger',
        );
      } else if (error.status === 474) {
        this.toastService.show('Création impossible, un contrat existe déjà sur la période.', 'danger');
      }
    }
  }

  private async updateContract(toSaveContract: ContractDto) {
    try {
      await this.contractsService.saveContract(toSaveContract);
      this.toastService.show('Le contrat a été modifié avec succès.', 'success');
      this.modal.close();
    } catch (error) {
      if (error.status === 473) {
        this.toastService.show(
          `Modification impossible, un contrat ${this.contractTypeLabel(toSaveContract.contractType)} non terminé existe déjà sur la période.`,
          'danger',
        );
      } else if (error.status === 474) {
        this.toastService.show('Modification impossible, un contrat existe déjà sur la période.', 'danger');
      } else if (error.status === 475) {
        this.toastService.show(`Le contrat n'est pas modifiable`, 'danger');
      }
    }
  }

  public onStatusChanged(status: ContractStatus, init: boolean = false): void {
    if (status) {
      !init && this.contractDetailsForm.controls.statusDate.setValue(this.currentDate);
      if (!labelsByWorkflowedTypedContractType.includes(this.contractDetailsForm.controls.contractType.value)) {
        if (status === ContractStatus.Done && !this.user?.groups.includes('ADMIN')) {
          this.contractDetailsForm.controls.beginDate.disable({ emitEvent: false });
          this.contractDetailsForm.controls.endDate.disable({ emitEvent: false });
          this.contractDetailsForm.controls.contractType.disable({ emitEvent: false });
          this.contractDetailsForm.controls.mainPurpose.disable({ emitEvent: false });
          this.contractDetailsForm.controls.project.disable({ emitEvent: false });
          this.contractDetailsForm.controls.actionsToPerform.disable({ emitEvent: false });
          this.contractDetailsForm.controls.achievement.disable({ emitEvent: false });
        } else {
          this.contractDetailsForm.controls.beginDate.enable({ emitEvent: false });
          this.contractDetailsForm.controls.endDate.enable({ emitEvent: false });
          this.contractDetailsForm.controls.contractType.enable({ emitEvent: false });
          this.contractDetailsForm.controls.mainPurpose.enable({ emitEvent: false });
          this.contractDetailsForm.controls.project.enable({ emitEvent: false });
          this.contractDetailsForm.controls.actionsToPerform.enable({ emitEvent: false });
          this.contractDetailsForm.controls.achievement.enable({ emitEvent: false });
        }
      }
      this.contractDetailsForm.updateValueAndValidity();
    }
  }

  public onTypeChanged(type: ContractType): void {
    if (type) {
      if (!WorkflowedContractType.includes(type)) {
        this.contractDetailsForm.setValidators(DateValidators.datesOrderValidator('beginDate', 'endDate'));
        this.contractDetailsForm.updateValueAndValidity();
        this.contractDetailsForm.controls.status.setValidators(Validators.required);
        this.contractDetailsForm.controls.status.enable({ emitEvent: false });
        this.contractDetailsForm.controls.achievement.enable();
        this.contractDetailsForm.controls.status.updateValueAndValidity();
      } else {
        this.contractDetailsForm.clearValidators();
        this.contractDetailsForm.setValidators(DateValidators.datesOrderValidator('beginDate', 'endDate'));
        this.contractDetailsForm.updateValueAndValidity();
        this.contractDetailsForm.controls.status.clearValidators();
        this.contractDetailsForm.controls.status.disable({ emitEvent: false });
        this.contractDetailsForm.controls.achievement.disable();
        this.contractDetailsForm.controls.achievement.setValue(null);
        this.contractDetailsForm.controls.status.updateValueAndValidity();
      }
    }
  }

  public async delete(): Promise<void> {
    const confirm: boolean = await this.openConfirmationModal();
    if (confirm) {
      this.modal.close();
      await this.contractsService.delete(this.contract.id, this.contract.contractType);
    }
  }

  private async openConfirmationModal(): Promise<boolean> {
    const modal: NgbModalRef = this.modalService.open(ConfirmationComponent, {
      backdrop: 'static',
      centered: true,
    });
    const modalComponent: ConfirmationComponent = modal.componentInstance as ConfirmationComponent;
    return await modalComponent.confirm('Êtes-vous sûr(e) de vouloir supprimer ce contrat ?');
  }

  private async openSignatureModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(SignatureComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: SignatureComponent = modal.componentInstance as SignatureComponent;
    modalComponent.title = 'Signature bénéficiaire';
    return modalComponent.confirm();
  }

  private async openCommentModal(): Promise<any> {
    const modal: NgbModalRef = this.modalService.open(CommentComponent, {
      backdrop: 'static',
      centered: true,
      size: 'md',
    });
    const modalComponent: CommentComponent = modal.componentInstance as CommentComponent;
    return modalComponent.confirm();
  }

  public async workflowAction(status: ContractTraceabilityStatus): Promise<void> {
    switch (status) {
      case ContractTraceabilityStatus.SignedBeneficiary: {
        const signatureDataURL: string = await this.openSignatureModal();
        if (signatureDataURL) {
          await this.updateStatus(
            new TraceabilityDto(this.contract.id, ContractTraceabilityStatus.SignedBeneficiary, undefined, signatureDataURL),
            'Signatures apposées, contrat maintenant en validation Métropole.',
            this.contract.contractType,
          );
        }
        break;
      }
      case ContractTraceabilityStatus.Unvalidated: {
        const comment: string = await this.openCommentModal();
        if (comment !== null) {
          await this.updateStatus(
            new TraceabilityDto(this.contract.id, ContractTraceabilityStatus.Unvalidated, comment, undefined),
            'Action réalisée avec succès.',
            this.contract.contractType,
          );
        }
        break;
      }
      default: {
        await this.updateStatus(
          new TraceabilityDto(this.contract.id, status, undefined, undefined),
          status === ContractTraceabilityStatus.SignedMetro && this.contract.status === ContractTraceabilityStatus.ValidatedMetro
            ? 'Contrat généré avec succès.'
            : 'Action réalisée avec succès.',
          this.contract.contractType,
        );
        break;
      }
    }
  }

  private async updateStatus(traceabilityDto: TraceabilityDto, success: string, contractType: string): Promise<void> {
    try {
      await this.contractsService.updateStatus(traceabilityDto, contractType);
      this.modal.close();
      this.toastService.show(success, 'success');
    } catch (error) {
      if (error.status === 438) {
        this.toastService.show('Erreur durant la génération du contrat. Des signatures sont manquantes.', 'danger');
      } else if (error.status === 440) {
        this.toastService.show('Veuillez enregistrer une signature dans votre profil.', 'danger');
      } else if (error.status === 461) {
        this.toastService.show('Erreur durant la génération du contrat. Le dossier du bénéficiaire est incomplet.', 'danger');
      } else if (error.status === 473) {
        this.toastService.show(
          `Modification impossible, un contrat ${this.contractTypeLabel(contractType)} non terminé existe déjà sur la période.`,
          'danger',
        );
      }
    }
  }

  public setDates() {
    this.contractTypeSelected = true;
    this.contractDetailsForm.controls.mainPurpose.enable();
    this.contractDetailsForm.controls.project.enable();
    this.contractDetailsForm.controls.actionsToPerform.enable();
    this.contractDetailsForm.controls.beginDate.enable();
    this.contractDetailsForm.controls.endDate.enable();
    if (labelsByWorkflowedTypedContractType.includes(this.contractDetailsForm.controls.contractType.value)) {
      this.contractDetailsForm.controls.status.disable();
      this.contractDetailsForm.controls.achievement.disable();
    } else {
      this.contractDetailsForm.controls.status.enable();
      this.contractDetailsForm.controls.achievement.enable();
    }

    if (this.contractDetailsForm.controls.contractType.value === ContractType.SocioPro) {
      this.contractDetailsForm.controls.beginDate.setValue(this.datesNewContract.beginDateContractSocioPro);
      this.contractDetailsForm.controls.endDate.setValue(this.datesNewContract.endDateContractSocioPro);
    } else if (this.contractDetailsForm.controls.contractType.value === ContractType.StartingIERContract) {
      this.contractDetailsForm.controls.beginDate.setValue(this.datesNewContract.beginDateContractIER);
      this.contractDetailsForm.controls.endDate.setValue(this.datesNewContract.endDateContractIER);
    } else {
      this.contractDetailsForm.controls.beginDate.setValue(this.datesNewContract.beginDateContractClassic);
      this.contractDetailsForm.controls.endDate.setValue(this.datesNewContract.endDateContractClassic);
    }
  }

  // *********************************************************************************************
  // * UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS *
  // * UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS UTILS *
  // *********************************************************************************************

  private contractTypeLabel(contractType: ContractType | string): string {
    if (contractType === ContractType.StartingIERContract) {
      return 'IER';
    }
    if (contractType === ContractType.SocioPro) {
      return 'socio-professionnel';
    }
    return '';
  }
}
